/**
 * Compose Message Component
 * Create new messages, reply, forward, or edit drafts
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Textarea,
  Text,
  Spinner,
  Card,
} from '@fluentui/react-components';
import {
  SendRegular,
  SaveRegular,
  DismissRegular,
  ArrowReplyRegular,
  ArrowForwardRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../../configurations/index.config';
import { getErrorMessage } from '../../../../types';
import PeoplePicker from '../components/PeoplePicker';
import AttachmentUploader, { AttachmentFile } from '../components/AttachmentUploader';
import RichTextEditor from '../components/RichTextEditor';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#ffffff',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '1200px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  required: {
    color: '#d13438',
  },
  actions: {
    display: 'flex',
    gap: '12px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  originalMessage: {
    marginTop: '24px',
    padding: '16px',
    border: '1px solid #e1dfdd',
    borderLeft: '4px solid #008272',
    borderRadius: '4px',
    backgroundColor: '#f9f9f9',
  },
  ccBccButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '-8px',
  },
});

interface Recipient {
  mailbox_id: number;
  user_id: number;
  friendly_alias: string;
  full_name: string;
  department: string;
  role: string;
}

interface ComposeData {
  to: Recipient[];
  cc: Recipient[];
  bcc: Recipient[];
  subject: string;
  body: string;
  attachments: AttachmentFile[];
}

export const ComposeFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [composeData, setComposeData] = useState<ComposeData>({
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    attachments: [],
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [originalMessage, setOriginalMessage] = useState<any>(null);
  const [composeMode, setComposeMode] = useState<
    'new' | 'reply' | 'replyall' | 'forward' | 'draft'
  >('new');

  useEffect(() => {
    const draftId = searchParams.get('draft');
    const replyId = searchParams.get('reply');
    const replyAllId = searchParams.get('replyall');
    const forwardId = searchParams.get('forward');

    if (draftId) {
      loadDraft(parseInt(draftId));
    } else if (replyId) {
      loadReply(parseInt(replyId), false);
    } else if (replyAllId) {
      loadReply(parseInt(replyAllId), true);
    } else if (forwardId) {
      loadForward(parseInt(forwardId));
    }
  }, [searchParams]);

  const loadDraft = async (draftId: number) => {
    try {
      setLoading(true);
      setComposeMode('draft');
      const response = await ApiClient.get(`/memo/draft/${draftId}`);

      if (response.data.status === 'success') {
        const draft = response.data.data;
        setComposeData({
          to: draft.recipients?.to || [],
          cc: draft.recipients?.cc || [],
          bcc: draft.recipients?.bcc || [],
          subject: draft.subject || '',
          body: draft.body || '',
          attachments: [],
        });
        setShowCc(draft.recipients?.cc?.length > 0);
        setShowBcc(draft.recipients?.bcc?.length > 0);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
      navigate('/account/memo/inbox');
    } finally {
      setLoading(false);
    }
  };

  const loadReply = async (messageId: number, replyAll: boolean) => {
    try {
      setLoading(true);
      setComposeMode(replyAll ? 'replyall' : 'reply');
      const response = await ApiClient.get(`/memo/message/${messageId}`);

      if (response.data.status === 'success') {
        const message = response.data.data;
        setOriginalMessage(message);

        const toRecipients = [
          {
            mailbox_id: message.sender.mailbox_id,
            user_id: message.sender.user_id,
            friendly_alias: message.sender.alias,
            full_name: message.sender.name,
            department: message.sender.department,
            role: '',
          },
        ];

        const ccRecipients = replyAll ? message.recipients?.cc || [] : [];

        setComposeData({
          to: toRecipients,
          cc: ccRecipients,
          bcc: [],
          subject: message.subject.startsWith('Re:') ? message.subject : `Re: ${message.subject}`,
          body: `\n\n--- Original Message ---\nFrom: ${message.sender.name} (${message.sender.alias})\nDate: ${message.sent_at}\nSubject: ${message.subject}\n\n${message.body}`,
          attachments: [],
        });
        setShowCc(replyAll && ccRecipients.length > 0);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
      navigate('/account/memo/inbox');
    } finally {
      setLoading(false);
    }
  };

  const loadForward = async (messageId: number) => {
    try {
      setLoading(true);
      setComposeMode('forward');
      const response = await ApiClient.get(`/memo/message/${messageId}`);

      if (response.data.status === 'success') {
        const message = response.data.data;
        setOriginalMessage(message);

        setComposeData({
          to: [],
          cc: [],
          bcc: [],
          subject: message.subject.startsWith('Fwd:') ? message.subject : `Fwd: ${message.subject}`,
          body: `\n\n--- Forwarded Message ---\nFrom: ${message.sender.name} (${message.sender.alias})\nDate: ${message.sent_at}\nSubject: ${message.subject}\n\n${message.body}`,
          attachments: [],
        });
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
      navigate('/account/memo/inbox');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (composeData.to.length === 0) {
      newToast('error', 'Please add at least one recipient');
      return;
    }

    if (!composeData.subject.trim()) {
      newToast('error', 'Please enter a subject');
      return;
    }

    if (!composeData.body.trim()) {
      newToast('error', 'Please enter a message');
      return;
    }

    try {
      setSending(true);

      // Step 1: Upload attachments first (if any)
      // Set Content-Type to undefined to remove the default 'application/json' header
      // This lets axios/browser automatically set multipart/form-data with correct boundary
      const attachmentIds: number[] = [];
      for (const att of composeData.attachments) {
        const formData = new FormData();
        formData.append('file', att.file);
        const uploadRes = await ApiClient.post('/memo/attachment/upload', formData, {
          headers: { 'Content-Type': undefined },
        });
        if (uploadRes.data.status === 'success') {
          attachmentIds.push(uploadRes.data.data.attachment_id);
        } else {
          newToast('error', `Failed to upload attachment: ${att.file.name}`);
          return;
        }
      }

      // Step 2: Build message payload
      const messagePayload: {
        to: number[];
        cc: number[];
        bcc: number[];
        subject: string;
        body: string;
        attachment_ids: number[];
        is_draft: boolean;
        in_reply_to?: string;
        forward_from?: string;
      } = {
        to: composeData.to.map(r => r.mailbox_id),
        cc: composeData.cc.map(r => r.mailbox_id),
        bcc: composeData.bcc.map(r => r.mailbox_id),
        subject: composeData.subject,
        body: composeData.body,
        attachment_ids: attachmentIds,
        is_draft: false,
      };

      if (composeMode === 'reply' || composeMode === 'replyall') {
        const messageId = searchParams.get('reply') || searchParams.get('replyall');
        if (messageId) {
          messagePayload.in_reply_to = messageId;
        }
      }

      if (composeMode === 'forward') {
        const messageId = searchParams.get('forward');
        if (messageId) {
          messagePayload.forward_from = messageId;
        }
      }

      // Step 3: Send message as JSON
      const response = await ApiClient.post('/memo/compose', messagePayload);

      if (response.data.status === 'success') {
        newToast('success', 'Message sent successfully');
        navigate('/account/memo/sent');
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSending(true);

      // Step 1: Upload attachments first (if any)
      // Set Content-Type to undefined to let axios auto-detect FormData
      const attachmentIds: number[] = [];
      for (const att of composeData.attachments) {
        const formData = new FormData();
        formData.append('file', att.file);
        const uploadRes = await ApiClient.post('/memo/attachment/upload', formData, {
          headers: { 'Content-Type': undefined },
        });
        if (uploadRes.data.status === 'success') {
          attachmentIds.push(uploadRes.data.data.attachment_id);
        } else {
          newToast('error', `Failed to upload attachment: ${att.file.name}`);
          return;
        }
      }

      const draftId = searchParams.get('draft');

      if (draftId) {
        // Update existing draft: PUT /memo/draft/:id
        const draftData = {
          to: composeData.to.map(r => r.mailbox_id),
          cc: composeData.cc.map(r => r.mailbox_id),
          bcc: composeData.bcc.map(r => r.mailbox_id),
          subject: composeData.subject,
          body: composeData.body,
          attachment_ids: attachmentIds,
        };

        const response = await ApiClient.put(`/memo/draft/${draftId}`, draftData);

        if (response.data.status === 'success') {
          newToast('success', 'Draft saved');
          navigate('/account/memo/drafts');
        }
      } else {
        // Create new draft: POST /memo/compose with is_draft: true
        const draftData = {
          to: composeData.to.map(r => r.mailbox_id),
          cc: composeData.cc.map(r => r.mailbox_id),
          bcc: composeData.bcc.map(r => r.mailbox_id),
          subject: composeData.subject || '(No Subject)',
          body: composeData.body,
          attachment_ids: attachmentIds,
          is_draft: true,
        };

        const response = await ApiClient.post('/memo/compose', draftData);

        if (response.data.status === 'success') {
          newToast('success', 'Draft saved');
          navigate('/account/memo/drafts');
        }
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  const getTitle = () => {
    switch (composeMode) {
      case 'reply':
        return 'Reply';
      case 'replyall':
        return 'Reply All';
      case 'forward':
        return 'Forward';
      case 'draft':
        return 'Edit Draft';
      default:
        return 'New Message';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>{getTitle()}</Text>
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={handleCancel} disabled={sending}>
            Cancel
          </Button>
          <Button
            appearance="subtle"
            icon={<SaveRegular />}
            onClick={handleSaveDraft}
            disabled={sending}
          >
            Save Draft
          </Button>
          <Button
            appearance="primary"
            icon={<SendRegular />}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.form}>
          <PeoplePicker
            label="To"
            selectedRecipients={composeData.to}
            onRecipientsChange={to => setComposeData({ ...composeData, to })}
            required
          />

          {!showCc && !showBcc && (
            <div className={styles.ccBccButtons}>
              <Button appearance="subtle" size="small" onClick={() => setShowCc(true)}>
                + Cc
              </Button>
              <Button appearance="subtle" size="small" onClick={() => setShowBcc(true)}>
                + Bcc
              </Button>
            </div>
          )}

          {showCc && (
            <PeoplePicker
              label="Cc"
              selectedRecipients={composeData.cc}
              onRecipientsChange={cc => setComposeData({ ...composeData, cc })}
            />
          )}

          {showBcc && (
            <PeoplePicker
              label="Bcc"
              selectedRecipients={composeData.bcc}
              onRecipientsChange={bcc => setComposeData({ ...composeData, bcc })}
            />
          )}

          <div className={styles.field}>
            <label className={styles.label}>
              Subject<span className={styles.required}> *</span>
            </label>
            <Input
              value={composeData.subject}
              onChange={(e, data) => setComposeData({ ...composeData, subject: data.value })}
              placeholder="Enter subject"
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Message<span className={styles.required}> *</span>
            </label>
            <RichTextEditor
              value={composeData.body}
              onChange={body => setComposeData({ ...composeData, body })}
              placeholder="Type your message here..."
              minHeight="350px"
            />
          </div>

          <AttachmentUploader
            attachments={composeData.attachments}
            onAttachmentsChange={attachments => setComposeData({ ...composeData, attachments })}
            maxFiles={5}
            maxSizePerFile={10 * 1024 * 1024}
          />

          {originalMessage && (
            <Card className={styles.originalMessage}>
              <Text weight="semibold" block>
                {composeMode === 'forward' ? 'Forwarded Message' : 'Original Message'}
              </Text>
              <Text size={200} block style={{ marginTop: '8px', color: '#605e5c' }}>
                From: {originalMessage.sender.name} ({originalMessage.sender.alias})
              </Text>
              <Text size={200} block style={{ color: '#605e5c' }}>
                Subject: {originalMessage.subject}
              </Text>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComposeFluentUI;
